import type { BusinessDate, ISODateTime } from './types';

/**
 * Business dates vs. calendar dates
 * ---------------------------------
 * A bar's "Thursday" often ends at 2:30 AM Friday. Every record that belongs to a night's
 * trading carries a business date. It is derived from the moment something happened, in
 * the venue's timezone, minus the configurable cutoff: anything before the cutoff time
 * belongs to the previous business date.
 *
 * Timestamps are stored as UTC ISO strings; business dates as YYYY-MM-DD. The two are
 * never mixed up by slicing an ISO string.
 */

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatterFor(timeZone);
    return true;
  } catch {
    return false;
  }
}

export function zonedParts(instant: Date, timeZone: string): ZonedParts {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

export function parseCutoff(cutoff: string): { hour: number; minute: number } {
  const m = /^(\d{1,2}):(\d{2})$/.exec(cutoff.trim());
  if (!m) throw new Error(`Invalid business-day cutoff "${cutoff}". Use HH:mm, e.g. 06:00.`);
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) throw new Error(`Invalid business-day cutoff "${cutoff}".`);
  return { hour, minute };
}

/** "16:00" → "4 p.m.", "06:30" → "6:30 a.m." (how the time reads on a sign). */
export function formatClock(time: string): string {
  const { hour, minute } = parseCutoff(time);
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}${minute ? `:${pad(minute)}` : ''} ${hour < 12 ? 'a.m.' : 'p.m.'}`;
}

/** The later of two "HH:mm" times. */
export function laterTime(a: string, b: string): string {
  const m = (t: string) => {
    const p = parseCutoff(t);
    return p.hour * 60 + p.minute;
  };
  return m(b) > m(a) ? b : a;
}

function pad(n: number, len = 2): string {
  return String(n).padStart(len, '0');
}

export function toBusinessDateString(year: number, month: number, day: number): BusinessDate {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

export function isBusinessDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** The business date an instant belongs to, for a venue timezone and cutoff. */
export function businessDateFor(instant: Date | ISODateTime, timeZone: string, cutoff: string): BusinessDate {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  const p = zonedParts(date, timeZone);
  const c = parseCutoff(cutoff);
  const minutesNow = p.hour * 60 + p.minute;
  const minutesCutoff = c.hour * 60 + c.minute;
  const calendar = toBusinessDateString(p.year, p.month, p.day);
  return minutesNow < minutesCutoff ? addDays(calendar, -1) : calendar;
}

export function addDays(date: BusinessDate, days: number): BusinessDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return toBusinessDateString(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** Whole days from `a` to `b` (b − a). */
export function daysBetween(a: BusinessDate, b: BusinessDate): number {
  const [ay, am, ad] = a.split('-').map(Number) as [number, number, number];
  const [by, bm, bd] = b.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekdayOf(date: BusinessDate): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * Converts a wall-clock time in a timezone to a UTC instant.
 * Iterates on the zone offset so it is correct across DST changes.
 */
export function zonedTimeToUtc(date: BusinessDate, time: string, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const { hour, minute } = parseCutoff(time);
  const wallAsUtc = Date.UTC(y, m - 1, d, hour, minute);
  let guess = wallAsUtc;
  for (let i = 0; i < 3; i++) {
    const p = zonedParts(new Date(guess), timeZone);
    const seenAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    const diff = seenAsUtc - wallAsUtc;
    if (diff === 0) break;
    guess -= diff;
  }
  return new Date(guess);
}

/** UTC instants bounding a business date: [start, end). */
export function businessDayWindow(date: BusinessDate, timeZone: string, cutoff: string): { start: Date; end: Date } {
  return {
    start: zonedTimeToUtc(date, cutoff, timeZone),
    end: zonedTimeToUtc(addDays(date, 1), cutoff, timeZone),
  };
}

export function compareBusinessDates(a: BusinessDate, b: BusinessDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Inclusive list of business dates from `from` to `to`. */
export function businessDateRange(from: BusinessDate, to: BusinessDate): BusinessDate[] {
  const out: BusinessDate[] = [];
  const n = daysBetween(from, to);
  for (let i = 0; i <= n; i++) out.push(addDays(from, i));
  return out;
}

export function formatBusinessDate(date: BusinessDate, locale = 'en-CA', style: 'short' | 'long' = 'short'): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    weekday: 'short',
    month: style === 'long' ? 'long' : 'short',
    day: 'numeric',
    year: style === 'long' ? 'numeric' : undefined,
  }).format(dt);
}

export function formatInstant(iso: ISODateTime, timeZone: string, locale = 'en-CA'): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}
